<template>
  <cc-splitter
    :tabs="tabs"
    :active-tab.sync="activeTabIdx"
    :open.sync="openAside"
    :min-width="360"
    :left-min-width="420"
    :initial-width="360"
    @size-changed="splitterSizeChanged"
    @primary-button-clicked="primaryButtonClicked"
  >

    <cc-loading :show="loading" />

    <!-- TODO, the reload button isn't hooked up to anything -->
    <cc-error :show="!loading && fatalError" />

    <b-card v-if="!loading && !fatalError">
      <b-row 
        cols="3" 
        class="mb-2"
      >
        <b-col class="mr-3 dashboard-title-col">
          <b-dropdown
            variant="link"
            class="dashboard-title-dropdown"
            :right="title > 10"
            :left="title <= 10"
          >
            <span 
              slot="button-content" 
              class="dashboard-title"
            >
              {{ title }}
            </span>
            <cc-menu 
              :items="titleDropdownOptions" 
              @action="titleDropdownAction"
            />
          </b-dropdown>
          <span
            v-if="unsavedChanges"
            v-b-tooltip.hover
            data-testid="lblUnsavedChanges"
            class="position-absolute edit-dashboard-unsaved-changes"
            :title="$t('dashboard:edit_page.unsaved_changes', 'Dashboard has unsaved changes')"
          >
            <i class="fa fa-exclamation-triangle text-warning"/>
          </span>
        </b-col>

        <b-col class="text-right mr-auto">
          <b-input-group
            v-if="stationControlVisible"
            class="d-inline-flex align-items-center station-control-group"
            :prepend="$t('common:single_word.station','Station')"
            data-testid="ddlDashboardStationVariables"
          >
            <b-form-select
              v-model="selectedStation[0]" 
              value-field="id"
              text-field="name"
              :options="stationControlList"
            />
          </b-input-group>

          <b-input-group
            v-if="daterangeControlVisible && daterangeRelativeControlVisible"
            class="d-inline-flex align-items-center date-range-control-group"
            :prepend="$t('common:phrase.date_range','Date Range')"
          >
            <b-form-input
              data-testid="txtDashboardDaterangeRelativeVariables"
              number
              no-wheel
              :step="1"
              :min="1"
              :value="selectedDaterange.options.value"
              @input="relativeDateRangeValueChanged"
            />

            <b-form-select
              data-testid="ddlDashboardDaterangeRelativeVariables"
              :value="selectedDaterange.options.unit"
              :options="daterangeConstants.relativeOptions"
              @change="relativeDateRangeUnitChanged"
            />
          </b-input-group>
          <b-input-group
            v-if="daterangeControlVisible && daterangeExactControlVisible"
            data-testid="ddlDashboardDaterangeExactVariables"
            class="d-inline-flex align-items-center date-range-control-group"
            :prepend="$t('common:phrase.date_range','Date Range')"
          >
            <cc-date-range-picker
              v-model="selectedDaterangePickerFormatted"
              opens="left"
              :linked-calendars="false"
              :append-to-body="true"
              :show-dropdowns="true"
              :ranges="false"
              :auto-apply="false"
              :time-picker="true"
            />
          </b-input-group>
        </b-col>
        <b-col class="refresh-variable-controls">
          <b-button 
            v-if="stationControlVisible || daterangeControlVisible"  
            squared 
            class="bg-light mb-1" 
            data-testid="btnDashboardVariablesRefresh"
            variant="outline-success"
            @click="dashboardReloadEvent"
          >
            <i class="fa fa-repeat" /> 
          </b-button>
        </b-col>
      </b-row>

      <div style="margin: -10px -10px 0 -10px;">
        <grid-layout
          :layout="layoutBeingDisplayed"
          :col-num="24"
          :row-height="40"
          :is-draggable="isGridDraggable"
          :is-resizable="isGridResizable"
          :is-mirrored="false"
          :vertical-compact="true"
          :auto-size="true"
          :margin="[10, 10]"
          :use-css-transforms="true"
          :breakpoints="gridBreakpoints"
          :cols="gridBreakpointCols"
          :responsive="true"
          @breakpoint-changed="gridBreakpointChangedEvent"
          @update:layout="updateLayoutEvent"
        >
          <grid-item 
            v-for="(layoutItem) of layoutBeingDisplayed"
            :key="layoutItem.i"
            :x="layoutItem.x"
            :y="layoutItem.y"
            :w="layoutItem.w"
            :h="layoutItem.h"
            :i="layoutItem.i"
            drag-allow-from=".card-header"
            @resize="globalResize"
            @container-resized="globalResize"
            @resized="globalResize"
            @moved="panelMoved"
          >
            <cc-tile-base
              :title="panelProperties[layoutItem.i].title"
              class="dashboard-panel-tile"
              no-padding
              :nav-items="panelDropdownItems(layoutItem.i)"
              :dropdown-left="layoutItem.x > 18 ? false : true"
              :menu-no-flip="true"
              @action="panelDropdownAction"
            >
              <visualization
                slot="body"
                :visualization-config="panelProperties[layoutItem.i].visualization"
                :stations="stations"
                :resize-trigger="globalResizeTrigger"
                :grid-max-cols="currentGridCols"
                :grid-width="layoutItem.w"
                :grid-x="layoutItem.x"
                :data="panelData[layoutItem.i].data"
                :data-loading="panelData[layoutItem.i].loading"
                :data-error="panelData[layoutItem.i].error"
                :total-measurements-matched="panelData[layoutItem.i].totalMeasurementsMatched"
                :uom-units="uom ? uom.units : []"
                @reloadClicked="refreshData"
              />
            </cc-tile-base>
          </grid-item>
        </grid-layout>
      </div>
    </b-card>

    <dashboard-metadata-aside
      v-if="level == 'dashboard'"
      slot="tab0"
      :save="level == 'dashboard' && activeTabIdx === 0 && tabSave"
      :currentDashboard="dashboard"
      @update:save="tabSave = $event"
      @close-aside="closeAside"
      @update-dashboard-metadata="updateDashboardMetadata"
    />

    <variables-aside
      v-if="level == 'dashboard'"
      slot="tab1"
      :save-trigger="level == 'dashboard' && activeTabIdx === 1 && tabSave"
      :variables="dashboardVariables"
      @update:save-trigger="tabSave = $event"
      @variables-updated="variablesUpdated"
    />

    <panel-settings
      v-if="level == 'panel'"
      slot="tab0"
      :save-trigger="level == 'panel' && activeTabIdx === 0 && tabSave"
      :panel="panelBeingEdited"
      @update:save-trigger="tabSave = $event"
      @panel-updated="panelUpdated"
    />

    <panel-data-settings
      v-if="level == 'panel'"
      slot="tab1"
      :save-trigger="level == 'panel' && activeTabIdx === 1 && tabSave"
      :panel="panelBeingEdited"
      :uom="uom"
      :station-variable-active="dashboardVariables && dashboardVariables.stations && dashboardVariables.stations.enabled"
      @update:save-trigger="tabSave = $event"
      @settings-updated="panelDataUpdated"
    />

    <visualization-aside
      v-if="level == 'panel'"
      slot="tab2"
      :save-trigger="level == 'panel' && activeTabIdx === 2 && tabSave"
      :panel="panelBeingEdited"
      @update:save-trigger="tabSave = $event"
      @visualization-updated="visualizationUpdated"
    />
  </cc-splitter>
</template>

<style>
.dashboard-title-col {
  flex-grow: 0;
  max-width: calc(100% - 20px);
}
.dashboard-title {
  font-size: 1.75rem;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  width: 100%;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dashboard-title-dropdown {
  margin-bottom: 1rem;
  width: 100%;
}
.dashboard-title-dropdown button {
  color: black;
  text-decoration: none;
  padding: 0;
  width: 100%;
}
.dashboard-title-dropdown button:hover {
  text-decoration: none;
}
.dashboard-title-dropdown button:focus {
  box-shadow: 0 0 0 0.2rem rgb(32 168 216 / 25%);
  text-decoration: none;
}
.dashboard-panel-tile {
  height: 100%;
  margin: 0;
}
.dashboard-panel-tile .tile-base__dropdown button {
  padding: 0;
  margin: 2px;
}
.vue-grid-item {
  /* MOBILE FIX */
  touch-action: none;
}

.reportrange-text{
  overflow : hidden;
  white-space: nowrap;
}
.edit-dashboard-unsaved-changes {
  font-size: 1.5rem;
  z-index: 1;
  padding: 0 0.3rem;
  margin-left: 10px;
  border-radius: 4px;
}
.edit-dashboard-unsaved-changes:hover {
  background-color: #fff6cc;
}

.station-control-group{
  max-width: 20em;
}
.date-range-control-group{
  max-width: 20em;
  flex-wrap: nowrap;
}
.refresh-variable-controls{
  text-align: right;
  flex-grow: 0;
}
.dashboard-title-dropdown .dropdown-toggle::after {
  vertical-align: 0.75em;
  margin-left: 0.055em;
}
</style>

<script>
import daterangeConstants from '../shared/daterange-constants';
import VueGridLayout from 'vue-grid-layout';
import DashboardMetadataAside from '@/components/DashboardMetadataAside';
import PanelSettings from '@/components/PanelSettings';
import PanelDataSettings from '@/components/PanelDataSettings';
import VariablesAside from '@/components/VariablesAside';
import VisualizationAside from '@/components/VisualizationAside';
import Visualization from '@/components/Visualization';
import api from '@/api';
import dataUtil from '../shared/data-utils';
import {
  getData as getMeasurementData,
  init as initDataManager
} from '@/shared/data-manager';

initDataManager(api);

const DEFAULT_PANEL = {
  title: 'New Panel',
  description: '',
  data: { sources: [] },
  visualization: {
    type: 'line',
    options: {
      display: {
        title: '',
        subtitle: '',
        showLegend: true,
        toggleChangeOverTime: false, // for the depth chart
      },
      leftAxis: {
        min: '',
        max: '',
        label: '',
        show: true,
        alignLabel: 'end',
      },
      rightAxis: {
        min: '',
        max: '',
        label: '',
        show: false,
        alignLabel: 'end',
      },
      xAxis: {
        show: true,
      },
      thresholdLines: [],
    },
  },
};
const MAX_DATE_RANGE = 1000 * 60 * 60 * 24 * 366.1;

export default {
  name: 'edit-dashboard',

  components: {
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
    DashboardMetadataAside,
    PanelSettings,
    PanelDataSettings,
    VariablesAside,
    VisualizationAside,
    Visualization,
  },

  data() {
    return {
      pageError: false,
      pageLoading: true,
      activeTabIdx: 0,
      openAside: false,
      activeTab: '',
      layout: [
        { x: 0, y: 0, w: 8, h: 6, i: 1 },
      ],
      layoutBeingDisplayed: [
        { x: 0, y: 0, w: 8, h: 6, i: 1 },
      ],
      panelProperties: {
        '1': JSON.parse(JSON.stringify(DEFAULT_PANEL)),
      },
      panelData: {
        '1': {
          loading: false,
          error: false,
          data: [],
          totalMeasurementsMatched: 0,
        },
      },
      isEdit: false,
      unsavedChanges : false,
      dashboard: null,
      tabSave: false,
      slug: '',
      panelBeingEdited: null,
      daterangeConstants: daterangeConstants,
      selectedStation: [], // So it's an easier transition to when we can select multiple at once
      selectedDaterange: {
        type: daterangeConstants.time_range_type.relativeDateRange,
        options: {
          milliseconds: 1000 * 60 * 60 * 24 * 2,
          unit: 'days',
          value: 2,
        },
      },
      dataRefreshKey: 0,
      turnoffGlobalResizeTimeout: null,
      globalResizeTrigger: false,
      refreshGlobalResizeTimeout: false,
      currentGridCols: 12,
      gridBreakpoints: { desktop: 480, mobile: 0 },
      gridBreakpointCols: { desktop: 12, mobile: 1 },
      isDisplayingMobile: false,
    };
  },

  computed: {
    isGridDraggable() {
      return !this.isDisplayingMobile;
    },
    isGridResizable() {
      return !this.isDisplayingMobile;
    },
    title() {
      return this.dashboard.name;
    },
    titleDropdownOptions() {
      return [
        {
          text: this.$t('common:single_word.settings', 'Settings'),
          action: 'settings',
          test_id: 'mniDashboardSettings'
        },
        {
          text: this.$t('common:single_word.variables', 'Variables'),
          action: 'variables',
          test_id: 'mniDashboardVariables'
        },
        {
          text: this.$t('dashboard:edit_page.title_dropdown.insert_panel','Insert Panel'),
          action: 'insert_panel',
          test_id: 'mniInsertPanel',
          security: 'dashboard..entity.update'
        },
        {
          text: this.$t('common:single_word.save', 'Save'),
          action: 'save',
          test_id: 'mniDashboardSave',
          security: 'dashboard..entity.update'
        },
        {
          text: this.$t('common:single_word.close', 'Close'),
          action: 'close',
          test_id: 'mniDashboardClose'
        },
      ].filter(dpnOpt => dpnOpt.security ? this.$s.can.access(dpnOpt.security) : true);
    },
    level() {
      if (this.activeTab == 'edit_dashboard_settings' || this.activeTab == 'edit_dashboard_variables') return 'dashboard';
      else return 'panel';
    },
    tabs() {
      return [
        {
          id: 'edit_dashboard_settings',
          title: this.$t('dashboard:edit_page.aside.tabs.dashboard_settings.title', 'Dashboard Settings'),
          disabled: false,
          icon: 'fa fa-pencil',
          testid: 'DashboardSettings',
          showTitle: true,
          showPrimaryButton: this.$s.can.access('dashboard..entity.update'),
          primaryButtonClass: 'fa-refresh',
          level: 'dashboard'
        },
        {
          id: 'edit_dashboard_variables',
          title: this.$t('dashboard:edit_page.aside.tabs.dashboard_variables.title', 'Variables'),
          disabled: false,
          icon: 'fa fa-calendar-o',
          testid: 'DashboardVariables',
          showTitle: true,
          showPrimaryButton: this.$s.can.access('dashboard..entity.update'),
          primaryButtonClass: 'fa-refresh',
          level: 'dashboard'
        },
        {
          id: 'edit_panel_settings',
          title: this.$t('dashboard:edit_page.aside.tabs.panel_settings.title', 'Panel Settings'),
          disabled: false,
          icon: 'fa fa-window-maximize',
          testid: 'PanelSettingsTab',
          showTitle: true,
          showPrimaryButton: this.$s.can.access('dashboard..entity.update') && this.panelBeingEdited,
          primaryButtonClass: 'fa-refresh',
          level: 'panel'
        },
        {
          id: 'edit_panel_data',
          title: this.$t('dashboard:edit_page.aside.tabs.panel_data_settings.title', 'Panel Data'),
          disabled: false,
          icon: 'fa fa-edit',
          testid: 'PanelDataSettingsTab',
          showTitle: true,
          showPrimaryButton: this.$s.can.access('dashboard..entity.update') && this.panelBeingEdited,
          primaryButtonClass: 'fa-refresh',
          level: 'panel'
        },
        {
          id: 'edit_panel_visualization',
          title: this.$t('dashboard:edit_page.aside.tabs.panel_visualization.title', 'Panel Visualization'),
          disabled: false,
          icon: 'fa fa-area-chart',
          testid: 'PanelVisualizationTab',
          showTitle: true,
          showPrimaryButton: this.$s.can.access('dashboard..entity.update') && this.panelBeingEdited,
          primaryButtonClass: 'fa-refresh',
          level: 'panel'
        },
      ].filter(tab => tab.level == this.level);
    },
    loading() {
      return this.pageLoading || this.$store.state.stationsLoading || this.$store.state.uomLoading;
    },
    stations() {
      return this.$store.state.stations;
    },
    uom() {
      return this.$store.state.uom;
    },
    fatalError() {
      return this.pageError || this.$store.state.stationsError || this.$store.state.uomError;
    },
    dashboardVariables(){
      return this.dashboard && this.dashboard.attributes ? this.dashboard.attributes.variables : {};
    },
    daterangeControlVisible(){
      return this.dashboard.attributes.variables.daterange.enabled || false;
    },
    daterangeRelativeControlVisible(){
      if(this.dashboard && this.dashboard.attributes.variables.daterange 
          && this.dashboard.attributes.variables.daterange.type == daterangeConstants.time_range_type.relativeDateRange){
        return true;
      }else return false;
    },
    daterangeExactControlVisible(){
      if(this.dashboard && this.dashboard.attributes.variables.daterange 
          && this.dashboard.attributes.variables.daterange.type == daterangeConstants.time_range_type.exactDateRange){
        return true;
      }else return false;
    },
    selectedDaterangePickerFormatted: {
      get: function() {
        return {
          startDate: this.selectedDaterange.options.epochStart ? new Date(this.selectedDaterange.options.epochStart) : null,
          endDate: this.selectedDaterange.options.epochEnd ? new Date(this.selectedDaterange.options.epochEnd) : null,
        };
      },
      set: function(val) {
        this.selectedDaterange.options = {
          epochStart: val.startDate ? new Date(val.startDate).valueOf() : null,
          epochEnd: val.endDate ? new Date(val.endDate).valueOf() : null,
        };
      },
    },
    stationControlVisible(){
      return this.dashboard.attributes.variables.stations.enabled || false;
    },
    stationControlList(){
      if(this.dashboard && this.dashboard.attributes && this.dashboard.attributes.variables && this.dashboard.attributes.variables.stations) return this.dashboard.attributes.variables.stations.selected.sort((a,b)=> a.name > b.name ? 1 : a.name < b.name ? -1 : 0 );
      else return [];
    },
    defaultVariables() {
      return {
        stations : {
          enabled : false,
          filters : [],
          stations: []
        },
        daterange : {
          enabled : true,
          type : 'relative_date_range',
          options : {
            milliseconds : 1.728e+8, // 2 days
            unit : 'days',
            value : '2'
          }
        }
      };
    },
  },

  watch: {
    activeTabIdx(newVal) {
      this.activeTab = this.tabs[newVal].id;
    },
    tabSave(){
      this.unsavedChanges = true;
    },
    selectedDaterange() {
      if (!this.loading) {
        this.refreshData();
      }
    },
    loading(newVal) {
      if (newVal === false) {
        this.refreshData();
      }
    },
  }, // END OF METHODS

  mounted() {
    if (!this.$store.state.stations && !this.$store.state.stationsError && !this.$store.state.stationsLoading) {
      this.$store.dispatch('refreshStations', {
        vendor: this.$Session.vendor,
        organizationId: this.$Session.organization_id,
        resourceId: this.$Session.sub,
      });
    }
    if (!this.$store.state.uom && !this.$store.state.uomError && !this.$store.state.uomLoading) {
      this.$store.dispatch('refreshUOM');
    }

    if (this.$route.params.slug) {

      this.isEdit = true;
      this.pageLoading = true;
      this.slug = this.$route.params.slug;

      const promise = api.getDashboard({
        vendor: this.$Session.vendor,
        organizationId: this.$Session.organization_id,
        dashboardId: this.slug,
      });

      promise.then(result => {
        this.createLayoutFromPanelConfig(result.data.attributes.panels);
        this.dashboard = result.data;
        if(this.dashboard.attributes.variables && this.dashboard.attributes.variables.daterange){
          this.selectedDaterange = {
            type: this.dashboard.attributes.variables.daterange.type,
            options: this.dashboard.attributes.variables.daterange.options,
          };
        }
        if(this.dashboard.attributes.variables && this.dashboard.attributes.variables.stations && this.dashboard.attributes.variables.stations.selected){
          this.selectedStation[0] = this.dashboard.attributes.variables.stations.selected.length ? this.dashboard.attributes.variables.stations.selected[0].id : null;
        }
        this.pageLoading = false;
      });

      promise.catch(err => {
        console.error(err);
        this.pageLoading = false;
        this.pageError = true;
      });
    } else {
      this.pageLoading = false;
      this.dashboard = {
        name: this.$t('dashboard:edit_page.new_dashboard.title','My Dashboard'),
        description: '',
        labels: [],
        attributes: {
          panels: this.generatePanelConfig(),
          variables : { ... this.defaultVariables }
        },
      };
      this.selectedDaterange = {
        type: this.dashboard.attributes.variables.daterange.type,
        options: this.dashboard.attributes.variables.daterange.options,
      };
    }
  },

  methods: {
    refreshData() {
      const validDateRange = this.isDateRangeValid();

      if (!validDateRange.valid) {
        this.$Toastr = {
          type: 'error',
          message: validDateRange.message,
        };
        return;
      }

      const requestKey = ++this.dataRefreshKey;

      for (const panelId in this.panelData) {
        this.panelData[panelId].loading = true;
        this.panelData[panelId].error = false;
      }

      const panelMeasurements = dataUtil.determinePanelStationMeasurements(this.panelProperties,
        this.stations, this.selectedStation);

      const panelPromises = getMeasurementData({
        panelMeasurements,
        stations: this.stations,
        force: false,
        timeRange: this.selectedDaterange,
      });

      for (const pm of panelMeasurements) {
        this.panelData[pm.panel_id].totalMeasurementsMatched = pm.total_measurements_matched;
      }

      for (const panelId in panelPromises) {
        panelPromises[panelId].then(result => {
          if (this.dataRefreshKey === requestKey) {
            this.panelData[panelId].loading = false;
            this.panelData[panelId].error = false;
            this.panelData[panelId].data = result;
            this.globalResize();
          }
        }).catch(err => {
          if (this.dataRefreshKey === requestKey) {
            this.panelData[panelId].loading = false;
            this.panelData[panelId].error = true;
            this.panelData[panelId].data = [];
          }
        });
      }
    },
    relativeDateRangeUnitChanged(newValue) {
      this.selectedDaterange.options.unit = newValue;
      this.updateRelativeDateRangeMilliseconds();
    },
    relativeDateRangeValueChanged(newValue) {
      this.selectedDaterange.options.value = newValue;
      this.updateRelativeDateRangeMilliseconds();
    },
    updateRelativeDateRangeMilliseconds() {
      this.selectedDaterange.options.milliseconds = Number(this.selectedDaterange.options.value) *
        daterangeConstants.relativeOptions.find(e => e.value === this.selectedDaterange.options.unit).millis;
    },
    dashboardReloadEvent() {
      this.refreshData();
    },
    isDateRangeValid() {
      const value = { valid: true, message: '' };

      if (this.daterangeRelativeControlVisible) {
        if (this.selectedDaterange.options.milliseconds > MAX_DATE_RANGE) {
          value.valid = false;
          value.message = this.$t('dashboard:edit_page.max_date_range_error', 'Date range must be less than one year.');
        }
      } else {
        const range = this.selectedDaterange.options.epochEnd - this.selectedDaterange.options.epochStart;
        if (range > MAX_DATE_RANGE) {
          value.valid = false;
          value.message = this.$t('dashboard:edit_page.max_date_range_error', 'Date range must be less than one year.');
        }
      }

      return value;
    },
    updateDashboardMetadata(changes) {
      this.dashboard.name = changes.name;
      this.dashboard.description = changes.description;
      this.dashboard.labels = changes.labels;
    },
    variablesUpdated(variables) {
      // this will trigger a refresh. If this is ever removed, please add
      // refreshData() here
      const newSelectedDaterange = JSON.parse(JSON.stringify(variables.daterange));
      delete newSelectedDaterange.enabled;
      this.selectedDaterange = newSelectedDaterange;

      this.dashboard.attributes.variables = {...variables};
      if (variables.stations.selected.length) this.selectedStation[0] = variables.stations.selected[0].id;
    },
    visualizationUpdated(visualization) {
      const oldVisualizationOptions = this.panelBeingEdited.visualization.options;
      this.panelBeingEdited.visualization = visualization;

      if (oldVisualizationOptions.display.showLegend !== visualization.options.display.showLegend) {
        this.globalResize();
      }
    },
    panelDataUpdated(settings) {
      this.panelBeingEdited.data = settings;
      this.refreshData();
    },
    panelUpdated(data) {
      this.panelBeingEdited.title = data.title;
      this.panelBeingEdited.description = data.description;
    },
    panelDropdownItems(id) {
      return [
        {
          text: this.$t('common:single_word.settings','Settings'),
          action: { name: 'settings', id },
          test_id: 'mniPanelSettings',
        },
        {
          text: this.$t('dashboard:edit_page.panel_dropdown.modify_data','Modify Data'),
          action: { name: 'data', id },
          test_id: 'mniPanelModify',
        },
        {
          text: this.$t('dashboard:edit_page.panel_dropdown.choose_visualization','Choose Visualization'),
          action: { name: 'visualization', id },
          test_id: 'mniPanelVisualization',
        },
        {
          text: this.$t('common:single_word.remove','Remove'),
          action: { name: 'delete', id },
          test_id: 'mniPanelRemove',
        },
      ];
    },
    titleDropdownAction(actionConfig) {
      switch (actionConfig.action) {
        case 'settings':
          this.openAnAside('edit_dashboard_settings');
          break;
        case 'variables':
          this.openAnAside('edit_dashboard_variables');
          break;
        case 'insert_panel':
          this.addPanel();
          break;
        case 'save':
          this.save();
          break;
        case 'close':
          this.$router.push({ name: 'Dashboards' });
          break;
      }
    },
    panelDropdownAction(actionConfig) {
      switch (actionConfig.name) {
        case 'delete':
          this.deletePanel(actionConfig.id);
          break;
        case 'settings':
          this.openAnAside('edit_panel_settings');
          this.panelBeingEdited = this.panelProperties[actionConfig.id];
          break;
        case 'data':
          this.openAnAside('edit_panel_data');
          this.panelBeingEdited = this.panelProperties[actionConfig.id];
          break;
        case 'visualization':
          this.openAnAside('edit_panel_visualization');
          this.panelBeingEdited = this.panelProperties[actionConfig.id];
          break;
      }
    },
    deletePanel(id) {
      this.deletePanelFromPersistentLayout(id);
      this.deletePanelFromDisplayedLayout(id);

      if (this.panelBeingEdited === this.panelProperties[id]) {
        this.panelBeingEdited = null;
      }
      delete this.panelProperties[id];
      this.unsavedChanges = true;
    },
    deletePanelFromPersistentLayout(id) {
      const match = this.layout.findIndex(e => e.i == id);
      if (match !== -1) this.layout.splice(match, 1);
    },
    deletePanelFromDisplayedLayout(id) {
      const match = this.layoutBeingDisplayed.findIndex(e => e.i == id);
      if (match !== -1) this.layoutBeingDisplayed.splice(match, 1);
    },
    addPanel() {
      if (this.layout.length >= 20) {
        this.$Toastr = {
          type: 'error',
          message: this.$t('dashboard.edit_page.panel_limit_reached', 'You have already added the maximum number of panels.'),
        };
      } else {
        const zeroZeroPanelIndex = this.layout.findIndex(e => e.x === 0 && e.y === 0);
        if (zeroZeroPanelIndex !== -1) {
          this.layout.splice(zeroZeroPanelIndex, 1, { ...this.layout[zeroZeroPanelIndex], y: 2 });
        }

        const newGridId = this.layout.reduce((p, c) => Number(c.i) > p ? Number(c.i) : p, 0) + 1;

        // must use $set here
        this.$set(this.panelProperties, newGridId, {
          ...JSON.parse(JSON.stringify(DEFAULT_PANEL)),
        });

        // must use $set here
        this.$set(this.panelData, newGridId, {
          loading: false,
          error: false,
          data: [],
          totalMeasurementsMatched: 0,
        });

        this.layout.push({
          x: 0,
          y: 0,
          w: 8,
          h: 6,
          i: newGridId,
        });
        this.layout = this.layout.sort((a, b) => a.y > b.y ? -1 : a.x > b.x ? -1 : 1);
        this.unsavedChanges = true;

        if (this.isDisplayingMobile) {
          this.layoutBeingDisplayed = this.generateMobileLayout(this.layout);
        } else {
          this.layoutBeingDisplayed = this.layout;
        }
      }
    },
    createLayoutFromPanelConfig(panelConfig) {
      const newLayout = [];
      const newPanelProperties = {};
      const newPanelData = {};

      for (const panel of panelConfig) {
        newLayout.push(JSON.parse(JSON.stringify(panel.grid_position)));

        newPanelProperties[panel.grid_position.i] = JSON.parse(JSON.stringify(panel));
        delete newPanelProperties[panel.grid_position.i].grid_position;

        newPanelData[panel.grid_position.i] = {
          loading: false,
          error: false,
          data: [],
          totalMeasurementsMatched: 0,
        };
      }

      this.panelProperties = newPanelProperties;
      this.panelData = newPanelData;
      this.layout = newLayout.sort((a, b) => a.y > b.y ? -1 : a.x > b.x ? -1 : 1);

      if (this.isDisplayingMobile) {
        this.layoutBeingDisplayed = this.generateMobileLayout(this.layout);
      } else {
        this.layoutBeingDisplayed = this.layout;
      }
    },
    generateMobileLayout(desktopLayout) {
      const mobileLayout = [ ...desktopLayout ];

      mobileLayout.sort((a, b) => {
        if (a.y > b.y) return 1;
        else if (a.y < b.y) return -1;
        else if (a.x > b.x) return 1;
        else return -1;
      });

      let nextY = 0;
      for (let i = 0; i < mobileLayout.length; i++) {
        mobileLayout[i] = {
          ...mobileLayout[i],
          y: nextY,
          w: 1,
          x: 0,
        };

        nextY += mobileLayout[i].h;
      }

      return mobileLayout;
    },
    generatePanelConfig() {
      const panels = [];

      for (const layoutItem of this.layout) {
        const panel = {
          grid_position: {
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
            i: layoutItem.i,
          },
          ...JSON.parse(JSON.stringify(this.panelProperties[layoutItem.i])),
        };

        panels.push(panel);
      }

      return panels;
    },
    save() {
      if (this.isEdit) {
        const newPanelConfig = this.generatePanelConfig();

        const updates = {
          name: this.dashboard.name,
          description: this.dashboard.description,
          labels: this.dashboard.labels,
          attributes: { 
            panels: newPanelConfig,
            variables : this.dashboard.attributes.variables
          },
        };

        const promise = api.updateDashboard({
          vendor: this.$Session.vendor,
          organizationId: this.$Session.organization_id,
          updates,
          slug: this.slug,
        });

        promise.then(result => {
          this.$Toastr = {
            type: 'success',
            message: this.$t('dashboard:edit_page.toastr.save_layout.success', 'Dashboard saved!'),
          };

          this.dashboard.attributes.panels = newPanelConfig;
          this.slug = result.data.slug;
          this.dashboard.slug = this.slug;

          this.unsavedChanges = false;

          // Need to update params so user doesn't get
          // 404 error if they refresh the page
          if (this.slug != this.$route.params.slug) {
            this.$router.replace({ params: {slug: this.slug}});
          }
        });

        promise.catch(err => {
          this.$Toastr = {
            type: 'error',
            message: this.$t('dashboard:edit_page.toastr.save_layout.error', 'An error occurred while trying to save changes.'),
          };
        });
      } else {
        const panelConfig = this.generatePanelConfig();

        const promise = api.createDashboard({
          vendor: this.$Session.vendor,
          organizationId: this.$Session.organization_id,
          dashboard: {
            name: this.dashboard.name,
            description: this.dashboard.description,
            attributes: { 
              panels: panelConfig,
              variables : this.dashboard.attributes.variables
            },
            labels: this.dashboard.labels,
          },
        });

        promise.then(result => {
          this.$Toastr = {
            type: 'success',
            message: this.$t('dashboard:edit_page.toastr.save_layout.success', 'Dashboard saved!'),
          };
          
          this.dashboard.slug = result.data.slug;
          this.dashboard.id = result.data.id;
          this.dashboard.attributes = { 
            panels: panelConfig,
            variables : this.dashboard.attributes.variables
          };

          this.unsavedChanges = false;

          this.isEdit = true;
          this.slug = result.data.slug;

          this.$router.replace({ name: 'EditDashboard', params: { slug: result.data.slug }});
        });

        promise.catch(err => {
          if (err && err.response && err.response.data && err.response.data.statusCode === 409) {
            this.$Toastr = {
              type: 'error',
              message: this.$t('dashboard:edit_page.toastr.save_layout.already_exists', 'A dashboard with that name already exists!'),
            };
          } else {
            this.$Toastr = {
              type: 'error',
              message: this.$t('dashboard:edit_page.toastr.save_layout.error', 'An error occurred while trying to save changes.'),
            };
          }
        });
      }
    },
    openAnAside(asideToOpen) {
      this.activeTab = asideToOpen;
      this.activeTabIdx = this.tabs.findIndex(t => t.id == this.activeTab);
      this.openAside = true;
    },
    splitterSizeChanged(newSize) {
      // will use in future
    },
    primaryButtonClicked() {
      this.tabSave = true;
    },
    closeAside(e) {
      this.openAside = false;
    },
    panelMoved() {
      this.layoutBeingDisplayed = this.layoutBeingDisplayed.sort((a, b) => a.y > b.y ? -1 : a.x > b.x ? -1 : 1);
      this.unsavedChanges = true;
    },
    // can't do resize on panel-per-panel basis.. it was too inefficient
    globalResize() {
      if (!this.turnoffGlobalResizeTimeout) {
        this.activateGlobalResize();
      } else {
        this.refreshGlobalResizeTimeout = true;
      }
    },
    activateGlobalResize() {
      this.globalResizeTrigger = true;
      this.turnoffGlobalResizeTimeout = setTimeout(() => {
        if (this.refreshGlobalResizeTimeout) {
          this.activateGlobalResize();
          this.refreshGlobalResizeTimeout = false;
        } else {
          this.globalResizeTrigger = false;
          this.turnoffGlobalResizeTimeout = null;
        }
      }, 2000);
    },
    gridBreakpointChangedEvent(newBreakpoint, newLayout) {
      this.currentGridCols = this.gridBreakpointCols[newBreakpoint];
      this.isDisplayingMobile = newBreakpoint === 'mobile';

      if (this.isDisplayingMobile) {
        this.layout = JSON.parse(JSON.stringify(this.layoutBeingDisplayed));
        newLayout.splice(0, 9999, ...this.generateMobileLayout(this.layout));
      } else {
        newLayout.splice(0, 9999, ...JSON.parse(JSON.stringify(this.layout)));
      }
    },
    updateLayoutEvent(newLayout) {
      this.layoutBeingDisplayed = newLayout;
      if (!this.isDisplayingMobile) {
        this.layout = JSON.parse(JSON.stringify(newLayout));
      }
    },
  },

  // Not working with keycloak session expiry
  // created() {
  //   window.onbeforeunload = () => {
  //     if(this.unsavedChanges){
  //       const answer = window.confirm(this.confirmNavigationPhrase);
  //       return answer;
  //     }
  //   };
  // },

  beforeRouteLeave (to, from , next) {
    if(this.unsavedChanges){
      const text = this.$t('common:phrase.unsaved_changes.page', 'The page has unsaved changes. If you leave, data entered will not be saved.');
      console.log('text', text);
      const answer = window.confirm(text);
      if (answer) {
        next();
      } else {
        next(false);
      }
    }else{
      next();
    }
  }
};
</script>
